import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Can } from '../contexts/PermissionsContext'
import { $api } from '../api/queryClient'
import { useReextractContractClauses } from '../hooks/useQueries'
import { useFormatters } from '../lib/format'
import type { components } from '../api/schema'

type Execution = components['schemas']['ContractExecution']

interface Props {
  org: string
  contractUid: string
  hasSourceDocument: boolean
  onOpenTab: (tab: string) => void
}

const STATUS_STYLE: Record<string, string> = {
  ok: 'border-green-500/40 bg-green-500/5',
  late: 'border-red-500/40 bg-red-500/5',
  unknown: 'border-border bg-muted/30',
  attention: 'border-amber-500/40 bg-amber-500/5',
}

const STATUS_BADGE: Record<string, 'success' | 'destructive' | 'secondary' | 'warning'> = {
  ok: 'success',
  late: 'destructive',
  unknown: 'secondary',
  attention: 'warning',
}

/**
 * The execution of a contract over the rolling window: visits, reports, spend
 * and renewal side by side (spec 2026-10-11-13). Each cell says in words what is
 * wrong, so the colour is never the only signal.
 */
export function ContractExecutionCard({ org, contractUid, hasSourceDocument, onOpenTab }: Props) {
  const { t } = useTranslation('contracts')
  const { formatDate, formatCurrency } = useFormatters()
  const reextract = useReextractContractClauses(org, contractUid)
  const [queued, setQueued] = useState(false)

  const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/execution', {
    params: { path: { orgCode: org, contractUid } },
  })
  if (!data) return null
  const e: Execution = data
  const has = (r: string) => e.reasons.includes(r as never)
  const months = Math.round(
    (new Date(e.window.to).getTime() - new Date(e.window.from).getTime()) / (30.4 * 24 * 3600 * 1000),
  )

  const visitsMain =
    e.visits.expected == null
      ? t('execution.visits_done_only', { done: e.visits.done })
      : t('execution.visits_ratio', { done: e.visits.done, expected: e.visits.expected })
  const spendMain =
    e.spend.contracted_per_year == null
      ? t('execution.spend_no_amount', { billed: formatCurrency(e.spend.billed, e.spend.currency) })
      : t('execution.spend_of', {
          billed: formatCurrency(e.spend.billed, e.spend.currency),
          contracted: formatCurrency(e.spend.contracted_per_year, e.spend.currency),
        })

  let renewalMain: string
  if (e.renewal.tacit) {
    renewalMain = e.renewal.denunciation_deadline
      ? t('execution.renewal_tacit_deadline', { date: formatDate(e.renewal.denunciation_deadline) })
      : t('execution.renewal_tacit')
  } else if (e.renewal.end_date) {
    renewalMain = t('execution.renewal_end', { date: formatDate(e.renewal.end_date) })
  } else {
    renewalMain = t('execution.renewal_none')
  }

  const warn = 'text-xs font-medium text-amber-700 dark:text-amber-400'
  const bad = 'text-xs font-medium text-red-700 dark:text-red-400'

  return (
    <Card className={STATUS_STYLE[e.status]} data-testid="contract-execution-card" data-status={e.status}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {t('execution.title', { months })}
          <Badge variant={STATUS_BADGE[e.status]} data-testid="contract-execution-status">
            {t(`execution.status_${e.status}`)}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div data-testid="contract-execution-visits" className="space-y-1">
          <p className="text-xs uppercase text-muted-foreground">{t('execution.visits')}</p>
          <p className="text-lg font-semibold">{visitsMain}</p>
          {has('no_cadence') && <p className={warn}>{t('execution.no_cadence')}</p>}
          {e.visits.overdue > 0 && <p className={bad}>{t('execution.overdue', { count: e.visits.overdue })}</p>}
          {has('visits_not_planned') && <p className={warn}>{t('execution.not_planned')}</p>}
          {e.visits.next_at && (
            <p className="text-xs text-muted-foreground">{t('execution.next', { date: formatDate(e.visits.next_at) })}</p>
          )}
          <Link
            to="/$org/interventions"
            params={{ org }}
            search={{ kind: undefined, status: undefined, contract_uid: contractUid }}
            className="text-xs text-primary underline"
            data-testid="contract-execution-visits-link"
          >
            {t('execution.see_visits')}
          </Link>
          {has('no_cadence') && (
            <Can perm="contracts.write">
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => onOpenTab('clauses')} data-testid="contract-execution-enter-cadence">
                  {t('execution.enter_cadence')}
                </Button>
                {hasSourceDocument && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={reextract.isPending || queued}
                    onClick={() => reextract.mutate({ onSuccess: () => setQueued(true) })}
                    data-testid="contract-execution-reread"
                  >
                    {queued ? t('execution.reread_queued') : t('execution.reread')}
                  </Button>
                )}
              </div>
            </Can>
          )}
        </div>

        <div data-testid="contract-execution-reports" className="space-y-1">
          <p className="text-xs uppercase text-muted-foreground">{t('execution.reports')}</p>
          <p className="text-lg font-semibold">{t('execution.reports_received', { count: e.reports.received })}</p>
          {e.reports.visits_without_report.length > 0 && (
            <p className={warn}>{t('execution.reports_missing', { count: e.reports.visits_without_report.length })}</p>
          )}
          <button type="button" className="text-xs text-primary underline" onClick={() => onOpenTab('reports')} data-testid="contract-execution-reports-link">
            {t('execution.see_reports')}
          </button>
        </div>

        <div data-testid="contract-execution-spend" className="space-y-1">
          <p className="text-xs uppercase text-muted-foreground">{t('execution.spend')}</p>
          <p className="text-lg font-semibold">{spendMain}</p>
          {has('over_budget') && <p className={bad}>{t('execution.over_budget')}</p>}
          <button
            type="button"
            className="text-xs text-primary underline"
            onClick={() => document.querySelector('[data-testid="contract-related-expenses"]')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="contract-execution-spend-link"
          >
            {t('execution.see_expenses')}
          </button>
        </div>

        <div data-testid="contract-execution-renewal" className="space-y-1">
          <p className="text-xs uppercase text-muted-foreground">{t('execution.renewal')}</p>
          <p className="text-lg font-semibold">{renewalMain}</p>
          {has('notice_window') && <p className={warn}>{t('execution.notice_window')}</p>}
        </div>
      </CardContent>
    </Card>
  )
}
